<!-- ItemForm/index.svelte — the unified form for items AND containers.
     An item with a container_type is a containable (box, bin, trunk, ...).
     The "can hold other items" toggle is one-way: once saved as a
     container, it stays a container (enforced server-side too). -->
<script>
  import { router } from '@inertiajs/svelte'
  import ImageUploader from '../ImageUploader/index.svelte'
  import SearchableSelect from '../SearchableSelect/index.svelte'

  let {
    item = {},
    property,
    spaces = [],
    containers = [],
    properties = [],
    errors = {},
    is_edit = false,
    start_as_container = false
  } = $props()

  // Convert space_id to string for consistent comparison
  const initialSpaceId = String(item?.space_id || '')
  let previousPropertyId = $state(property?.id || item?.property_id)

  let form = $state({
    name: item.name || '',
    description: item.description || '',
    property_id: property?.id || item.property_id || '',
    space_id: initialSpaceId,
    container_id: item.container_id || '',
    container_type: item.container_type || (start_as_container && !is_edit ? 'box' : ''),
    material: item.material || '',
    category: item.category || 'other',
    subcategory: item.subcategory || '',
    initial_quantity: item.initial_quantity || 1,
    condition: item.condition || 'good',
    status: item.status || 'in_use',
    brand: item.brand || '',
    model_number: item.model_number || '',
    serial_number: item.serial_number || '',
    purchase_date: item.purchase_date || '',
    purchase_price: item.purchase_price || '',
    current_value: item.current_value || '',
    warranty_expiration: item.warranty_expiration || '',
    dimensions: item.dimensions || '',
    weight: item.weight || '',
    color: item.color || '',
    is_valuable: item.is_valuable || false,
    is_favorite: item.is_favorite || false,
    notes: item.notes || ''
  })

  // Newly chosen/captured File objects to upload, and the ids of already-saved
  // images the user removed in this session.
  let newImageFiles = $state([])
  let existingImages = $state(item?.images || [])
  let removedImageIds = $state([])

  // Containable state. The toggle locks permanently once a container has
  // been saved — a container is always a container.
  let canContain = $state(!!form.container_type)
  const containLocked = is_edit && !!item.container_type

  function toggleCanContain() {
    if (containLocked) return
    canContain = !canContain
    if (canContain) {
      if (!form.container_type) form.container_type = 'box'
      form.initial_quantity = 1
    } else {
      form.container_type = ''
      form.material = ''
    }
  }

  // ImageUploader reports the full list of newly chosen files on every change.
  function handleImagesChange(files) {
    newImageFiles = files
  }

  // Drop a previously-saved image from the preview and remember its id so the
  // server can detach it on submit.
  function handleRemoveExisting(id) {
    existingImages = existingImages.filter((img) => img.id !== id)
    removedImageIds = [...removedImageIds, id]
  }

  // Parent options: a record can't live inside itself or its own subtree,
  // and containers can't be placed under a parent already at max depth (3).
  const parentOptions = $derived(containers.filter((candidate) => {
    if (candidate.id === item.id) return false
    if (
      item.materialized_path &&
      candidate.materialized_path?.startsWith(`${item.materialized_path}/`)
    ) {
      return false
    }
    if (canContain) {
      const depth = candidate.materialized_path
        ? candidate.materialized_path.split('/').length
        : 1
      if (depth >= 3) return false
    }
    return true
  }))

  // Option lists for the searchable selects (sub = the container's space name).
  const containerOptions = $derived(
    parentOptions.map((c) => ({
      value: c.id,
      label: c.label,
      sub: spaces.find((s) => String(s.id) === String(c.space_id))?.name
    }))
  )
  const propertyOptions = $derived(properties.map((p) => ({ value: p.id, label: p.name })))
  const spaceOptions = $derived(spaces.map((s) => ({ value: String(s.id), label: s.name })))

  // Update property and space when the parent container changes
  $effect(() => {
    if (form.container_id) {
      const selectedContainer = containers.find(c => c.id === form.container_id)
      if (selectedContainer) {
        form.property_id = selectedContainer.property_id
        form.space_id = selectedContainer.space_id
      }
    }
  })

  // When the property actually changes, clear the space and reload the space
  // list for the new property. The previousPropertyId guard means this does
  // NOT fire on initial mount, so a seeded space (e.g. when adding an item
  // from inside a container) is preserved.
  $effect(() => {
    if (form.property_id && form.property_id !== previousPropertyId) {
      previousPropertyId = form.property_id
      form.space_id = ''
      router.reload({
        only: ['spaces'],
        data: { property_id: form.property_id }
      })
    }
  })

  const categories = [
    'electronics',
    'furniture',
    'clothing',
    'kitchen',
    'tools',
    'sports',
    'books',
    'documents',
    'storage',
    'other'
  ]

  const containerTypes = [
    'box',
    'bag',
    'basket',
    'bin',
    'cabinet',
    'case',
    'chest',
    'closet',
    'crate',
    'drawer',
    'dresser',
    'rack',
    'shelf',
    'tote',
    'trunk',
    'wardrobe',
    'other'
  ]

  const conditions = [
    'new',
    'like_new',
    'good',
    'fair',
    'poor',
    'broken'
  ]

  // Ordered as a lifecycle: active first, then "left your inventory"
  // (sold/donated/gifted), then disposal (recycled/trashed) at the end.
  const statuses = [
    'in_use',
    'stored',
    'lent',
    'borrowed',
    'lost',
    'stolen',
    'sold',
    'donated',
    'gifted',
    'recycled',
    'trashed'
  ]

  // Option shape SearchableSelect expects ({ value, label }).
  const statusOptions = $derived(statuses.map((s) => ({ value: s, label: formatLabel(s) })))
  const categoryOptions = $derived(categories.map((c) => ({ value: c, label: formatLabel(c) })))
  const conditionOptions = $derived(conditions.map((c) => ({ value: c, label: formatLabel(c) })))

  const heading = $derived(
    canContain
      ? (is_edit ? 'Edit Container' : 'Add Container')
      : (is_edit ? 'Edit Item' : 'Add New Item')
  )

  function handleSubmit(event) {
    event.preventDefault()

    // Containers are always exactly one container
    if (canContain) form.initial_quantity = 1

    // Use FormData to handle file uploads
    const formData = new FormData()

    // Add all form fields to FormData, using item[] prefix for nested attributes
    Object.entries(form).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Append with item[] prefix for Rails to correctly nest parameters
        formData.append(`item[${key}]`, value)
      }
    })

    // New images are appended; existing ones are preserved server-side unless
    // their id is listed in removed_image_ids.
    newImageFiles.forEach((file) => {
      formData.append('item[images][]', file)
    })
    removedImageIds.forEach((id) => {
      formData.append('removed_image_ids[]', id)
    })

    const options = {
      onError: (e) => {
        errors = e
      },
      forceFormData: true
    }

    if (is_edit) {
      router.put(`/properties/${item.property_id}/items/${item.id}`, formData, options)
    } else {
      router.post(`/properties/${form.property_id}/items`, formData, options)
    }
  }

  function formatLabel(str) {
    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }
</script>

<!-- Per-status icon for the status SearchableSelect. One small SVG per value;
     falls back to a generic dot for anything unmapped. -->
{#snippet statusIcon(option)}
  {#if option.value === 'in_use'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="9"/></svg>
  {:else if option.value === 'stored'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5"/><path d="M12 13v8"/></svg>
  {:else if option.value === 'lent'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>
  {:else if option.value === 'borrowed'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="M11 6l-6 6 6 6"/></svg>
  {:else if option.value === 'lost'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 3.5"/><path d="M12 17h.01"/></svg>
  {:else if option.value === 'stolen'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
  {:else if option.value === 'sold'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2a2 2 0 0 1-.6-1.4V5a2 2 0 0 1 2-2h6.6a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.8z"/><path d="M7.5 7.5h.01"/></svg>
  {:else if option.value === 'donated'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 6.6a4.5 4.5 0 0 0-6.4 0L12 9l-2.4-2.4a4.5 4.5 0 1 0-6.4 6.4L12 21.8l8.8-8.8a4.5 4.5 0 0 0 0-6.4z"/></svg>
  {:else if option.value === 'gifted'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4"/><path d="M12 8v13"/><path d="M19 12v9H5v-9"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>
  {:else if option.value === 'recycled'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
  {:else if option.value === 'trashed'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6l-.9 13a2 2 0 0 1-2 1.9H7.9a2 2 0 0 1-2-1.9L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
  {:else}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"/></svg>
  {/if}
{/snippet}

<!-- Per-category icon for the category SearchableSelect. -->
{#snippet categoryIcon(option)}
  {#if option.value === 'electronics'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="13" rx="1.5"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>
  {:else if option.value === 'furniture'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/><path d="M3 11h18a1 1 0 0 1 1 1v4H2v-4a1 1 0 0 1 1-1z"/><path d="M5 16v3"/><path d="M19 16v3"/></svg>
  {:else if option.value === 'clothing'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3l3 2 3-2 5 4-3 3-1-1v9H8v-9l-1 1-3-3 5-4z"/></svg>
  {:else if option.value === 'kitchen'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 2v7a3 3 0 0 0 6 0V2"/><path d="M10 2v20"/><path d="M18 2c-1.5 0-3 2-3 5s1.5 5 3 5v10"/></svg>
  {:else if option.value === 'tools'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
  {:else if option.value === 'sports'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 0 0 18"/><path d="M12 3a14 14 0 0 1 0 18"/></svg>
  {:else if option.value === 'books'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v18H6.5A2.5 2.5 0 0 0 4 22.5z"/><path d="M4 4.5A2.5 2.5 0 0 0 6.5 7H20"/></svg>
  {:else if option.value === 'documents'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8"/><path d="M8 17h8"/></svg>
  {:else if option.value === 'storage'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5"/></svg>
  {:else}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12h8"/></svg>
  {/if}
{/snippet}

<!-- Per-condition icon: a signal-strength style ramp from new → broken. -->
{#snippet conditionIcon(option)}
  {#if option.value === 'new'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
  {:else if option.value === 'like_new'}
    <!-- chevrons-up: "near the top" — a notch below New's star -->
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 11 12 6 7 11"/><polyline points="17 18 12 13 7 18"/></svg>
  {:else if option.value === 'good'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/><path d="M9 9h.01"/><path d="M15 9h.01"/></svg>
  {:else if option.value === 'fair'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 14h8"/><path d="M9 9h.01"/><path d="M15 9h.01"/></svg>
  {:else if option.value === 'poor'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 15s1.5-2 4-2 4 2 4 2"/><path d="M9 9h.01"/><path d="M15 9h.01"/></svg>
  {:else if option.value === 'broken'}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L4.5 12.5 11 13l-1 9 9.5-11.5L13 10z"/></svg>
  {:else}
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"/></svg>
  {/if}
{/snippet}

<div class="max-w-2xl mx-auto p-4">
  <h1 class="text-2xl font-bold mb-6">{heading}</h1>

  <form onsubmit={handleSubmit} class="space-y-6">
    <!-- Basic Information -->
    <div class="bg-white shadow rounded-lg p-6">
      <h2 class="text-lg font-semibold mb-4">Basic Information</h2>

      <div class="grid grid-cols-1 gap-4">
        <div>
          <ImageUploader
            label={canContain ? 'Container Images' : 'Item Images'}
            error={errors.images}
            existingImages={existingImages}
            onChange={handleImagesChange}
            onRemoveExisting={handleRemoveExisting}
          />
        </div>

        <div>
          <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            id="name"
            bind:value={form.name}
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
            required
          />
          {#if errors.name}
            <p class="mt-1 text-sm text-red-600">{errors.name}</p>
          {/if}
        </div>

        <!-- Containable toggle: one-way once saved -->
        <div class="rounded-lg border {canContain ? 'border-brand-200 bg-brand-50' : 'border-gray-200 bg-gray-50'} p-4">
          <div class="flex items-start gap-3">
            <input
              type="checkbox"
              id="can_contain"
              checked={canContain}
              onchange={toggleCanContain}
              disabled={containLocked}
              class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600 disabled:opacity-60"
            />
            <div>
              <label for="can_contain" class="block text-sm font-semibold text-gray-900">
                This can hold other items
              </label>
              <p class="mt-0.5 text-xs text-gray-500">
                {#if containLocked}
                  This is a container. Once an item is a container, it can't be changed back.
                {:else if canContain}
                  Saved as a container — this can't be reverted later.
                {:else}
                  Turn on for boxes, bins, cabinets and anything else that stores things.
                {/if}
              </p>
            </div>
          </div>

          {#if canContain}
            <div class="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label for="container_type" class="block text-sm font-medium text-gray-700">Container Type</label>
                <select
                  id="container_type"
                  bind:value={form.container_type}
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
                >
                  {#each containerTypes as type}
                    <option value={type}>{formatLabel(type)}</option>
                  {/each}
                </select>
                {#if errors.container_type}
                  <p class="mt-1 text-sm text-red-600">{errors.container_type}</p>
                {/if}
              </div>

              <div>
                <label for="material" class="block text-sm font-medium text-gray-700">Material</label>
                <input
                  type="text"
                  id="material"
                  bind:value={form.material}
                  placeholder="e.g., Plastic, Wood, Metal"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
                />
                {#if errors.material}
                  <p class="mt-1 text-sm text-red-600">{errors.material}</p>
                {/if}
              </div>
            </div>
          {/if}
        </div>

        <div>
          <label for="description" class="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            id="description"
            bind:value={form.description}
            rows="3"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
          ></textarea>
          {#if errors.description}
            <p class="mt-1 text-sm text-red-600">{errors.description}</p>
          {/if}
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="category" class="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <SearchableSelect
              id="category"
              value={form.category}
              options={categoryOptions}
              placeholder="Select a category"
              onChange={(v) => (form.category = v)}
              renderIcon={categoryIcon}
            />
            {#if errors.category}
              <p class="mt-1 text-sm text-red-600">{errors.category}</p>
            {/if}
          </div>

          <div>
            <label for="subcategory" class="block text-sm font-medium text-gray-700">Subcategory</label>
            <input
              type="text"
              id="subcategory"
              bind:value={form.subcategory}
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
            />
            {#if errors.subcategory}
              <p class="mt-1 text-sm text-red-600">{errors.subcategory}</p>
            {/if}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="initial_quantity" class="block text-sm font-medium text-gray-700">
              {is_edit ? 'Total quantity' : 'Quantity'}
            </label>
            <input
              type="number"
              id="initial_quantity"
              bind:value={form.initial_quantity}
              min="1"
              disabled={canContain}
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600 disabled:bg-gray-100 disabled:text-gray-500"
              required
            />
            {#if canContain}
              <p class="mt-1 text-sm text-gray-500">Each container is its own record</p>
            {:else if is_edit}
              <p class="mt-1 text-sm text-gray-500">Use the quantity card on the item page to mark units as used.</p>
            {/if}
            {#if errors.initial_quantity}
              <p class="mt-1 text-sm text-red-600">{errors.initial_quantity}</p>
            {/if}
          </div>

          <div>
            <label for="condition" class="block text-sm font-medium text-gray-700 mb-1">Condition</label>
            <SearchableSelect
              id="condition"
              value={form.condition}
              options={conditionOptions}
              placeholder="Select a condition"
              onChange={(v) => (form.condition = v)}
              renderIcon={conditionIcon}
            />
            {#if errors.condition}
              <p class="mt-1 text-sm text-red-600">{errors.condition}</p>
            {/if}
          </div>
        </div>

        <div>
          <label for="status" class="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <SearchableSelect
            id="status"
            value={form.status}
            options={statusOptions}
            placeholder="Select a status"
            onChange={(v) => (form.status = v)}
            renderIcon={statusIcon}
          />
          {#if errors.status}
            <p class="mt-1 text-sm text-red-600">{errors.status}</p>
          {/if}
        </div>
      </div>
    </div>

    <!-- Location Information -->
    <div class="bg-white shadow rounded-lg p-6">
      <h2 class="text-lg font-semibold mb-4">Location</h2>

      <div class="grid grid-cols-1 gap-4">
        <div>
          <span class="block text-sm font-medium text-gray-700 mb-1">
            {canContain ? 'Parent Container' : 'Container'}
          </span>
          <SearchableSelect
            value={form.container_id}
            options={containerOptions}
            placeholder={canContain ? 'No parent container' : 'Select a container (optional)'}
            noneLabel={canContain ? 'No parent container' : 'No container'}
            onChange={(v) => (form.container_id = v)}
          />
          {#if canContain}
            <p class="mt-1 text-sm text-gray-500">Containers can nest up to 3 levels deep</p>
          {/if}
          {#if errors.container_id || errors.parent}
            <p class="mt-1 text-sm text-red-600">{errors.container_id || errors.parent}</p>
          {/if}
        </div>

        <div>
          <span class="block text-sm font-medium text-gray-700 mb-1">Property</span>
          <SearchableSelect
            value={form.property_id}
            options={propertyOptions}
            placeholder="Select a property"
            disabled={!!form.container_id}
            onChange={(v) => (form.property_id = v)}
          />
          {#if form.container_id}
            <p class="mt-1 text-sm text-gray-500">Inherited from container</p>
          {/if}
          {#if errors.property_id}
            <p class="mt-1 text-sm text-red-600">{errors.property_id}</p>
          {/if}
        </div>

        <div>
          <span class="block text-sm font-medium text-gray-700 mb-1">Space</span>
          <SearchableSelect
            value={form.space_id}
            options={spaceOptions}
            placeholder="Select a space"
            disabled={!!form.container_id || !form.property_id}
            onChange={(v) => (form.space_id = v)}
          />
          {#if form.container_id}
            <p class="mt-1 text-sm text-gray-500">Inherited from container</p>
          {/if}
          {#if errors.space_id}
            <p class="mt-1 text-sm text-red-600">{errors.space_id}</p>
          {/if}
        </div>
      </div>
    </div>

    <!-- Additional Details -->
    <div class="bg-white shadow rounded-lg p-6">
      <h2 class="text-lg font-semibold mb-4">Additional Details</h2>

      <div class="grid grid-cols-1 gap-4">
        {#if !canContain}
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="brand" class="block text-sm font-medium text-gray-700">Brand</label>
              <input
                type="text"
                id="brand"
                bind:value={form.brand}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
              />
              {#if errors.brand}
                <p class="mt-1 text-sm text-red-600">{errors.brand}</p>
              {/if}
            </div>

            <div>
              <label for="model_number" class="block text-sm font-medium text-gray-700">Model Number</label>
              <input
                type="text"
                id="model_number"
                bind:value={form.model_number}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
              />
              {#if errors.model_number}
                <p class="mt-1 text-sm text-red-600">{errors.model_number}</p>
              {/if}
            </div>
          </div>

          <div>
            <label for="serial_number" class="block text-sm font-medium text-gray-700">Serial Number</label>
            <input
              type="text"
              id="serial_number"
              bind:value={form.serial_number}
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
            />
            {#if errors.serial_number}
              <p class="mt-1 text-sm text-red-600">{errors.serial_number}</p>
            {/if}
          </div>
        {/if}

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="purchase_date" class="block text-sm font-medium text-gray-700">Purchase Date</label>
            <input
              type="date"
              id="purchase_date"
              bind:value={form.purchase_date}
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
            />
            {#if errors.purchase_date}
              <p class="mt-1 text-sm text-red-600">{errors.purchase_date}</p>
            {/if}
          </div>

          <div>
            <label for="purchase_price" class="block text-sm font-medium text-gray-700">Purchase Price</label>
            <input
              type="number"
              id="purchase_price"
              bind:value={form.purchase_price}
              step="0.01"
              min="0"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
            />
            {#if errors.purchase_price}
              <p class="mt-1 text-sm text-red-600">{errors.purchase_price}</p>
            {/if}
          </div>
        </div>

        {#if !canContain}
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="current_value" class="block text-sm font-medium text-gray-700">Current Value</label>
              <input
                type="number"
                id="current_value"
                bind:value={form.current_value}
                step="0.01"
                min="0"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
              />
              {#if errors.current_value}
                <p class="mt-1 text-sm text-red-600">{errors.current_value}</p>
              {/if}
            </div>

            <div>
              <label for="warranty_expiration" class="block text-sm font-medium text-gray-700">Warranty Expiration</label>
              <input
                type="date"
                id="warranty_expiration"
                bind:value={form.warranty_expiration}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
              />
              {#if errors.warranty_expiration}
                <p class="mt-1 text-sm text-red-600">{errors.warranty_expiration}</p>
              {/if}
            </div>
          </div>
        {/if}

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="dimensions" class="block text-sm font-medium text-gray-700">Dimensions</label>
            <input
              type="text"
              id="dimensions"
              bind:value={form.dimensions}
              placeholder="e.g., 10x20x30 cm"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
            />
            {#if errors.dimensions}
              <p class="mt-1 text-sm text-red-600">{errors.dimensions}</p>
            {/if}
          </div>

          <div>
            <label for="weight" class="block text-sm font-medium text-gray-700">Weight (kg)</label>
            <input
              type="number"
              id="weight"
              bind:value={form.weight}
              step="0.01"
              min="0"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
            />
            {#if errors.weight}
              <p class="mt-1 text-sm text-red-600">{errors.weight}</p>
            {/if}
          </div>
        </div>

        <div>
          <label for="color" class="block text-sm font-medium text-gray-700">Color</label>
          <input
            type="text"
            id="color"
            bind:value={form.color}
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
          />
          {#if errors.color}
            <p class="mt-1 text-sm text-red-600">{errors.color}</p>
          {/if}
        </div>

        <div>
          <label for="notes" class="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            id="notes"
            bind:value={form.notes}
            rows="3"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-600 focus:ring-brand-600"
          ></textarea>
          {#if errors.notes}
            <p class="mt-1 text-sm text-red-600">{errors.notes}</p>
          {/if}
        </div>

        <div class="flex items-center gap-4">
          <div class="flex items-center">
            <input
              type="checkbox"
              id="is_valuable"
              bind:checked={form.is_valuable}
              class="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
            />
            <label for="is_valuable" class="ml-2 block text-sm text-gray-700">
              Mark as Valuable
            </label>
          </div>

          <div class="flex items-center">
            <input
              type="checkbox"
              id="is_favorite"
              bind:checked={form.is_favorite}
              class="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
            />
            <label for="is_favorite" class="ml-2 block text-sm text-gray-700">
              Mark as Favorite
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-4">
      <a
        href={`/properties/${property?.id || form.property_id}`}
        class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        Cancel
      </a>
      <button
        type="submit"
        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-600"
      >
        {is_edit ? `Update ${canContain ? 'Container' : 'Item'}` : `Create ${canContain ? 'Container' : 'Item'}`}
      </button>
    </div>
  </form>
</div>
